import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, CreditCardIcon, ShieldIcon, CheckIcon, Loader2Icon } from './BrandIcons';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { C, F } from '../constants';
import { PaymentLoader } from './BrandedLoader';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Load Razorpay script
const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

export const PaymentCheckout = ({ 
  isOpen, 
  onClose, 
  selectedPlan, 
  selectedCreditPack,
  billingCycle: propBillingCycle = 'monthly',
  onBillingCycleChange,
  onSuccess,
  // New props for dashboard usage
  type = 'subscription', // 'subscription' or 'credits'
  plan,
  onPlanSelect,
  plans = [],
  creditPack,
  onCreditPackSelect,
  creditPacks = [],
  user: propUser,
  currency: propCurrency = 'INR',
  onCurrencyChange
}) => {
  const { theme } = useTheme();
  const { user: authUser } = useAuth();
  const user = propUser || authUser;
  const [loading, setLoading] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [error, setError] = useState('');
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [successData, setSuccessData] = useState(null);
  const [currency, setCurrencyState] = useState(propCurrency);
  const [billingCycle, setBillingCycleState] = useState(propBillingCycle);
  
  // Use either the new props or legacy props
  const activePlan = plan || selectedPlan;
  const activeCreditPack = creditPack || selectedCreditPack;
  
  // Helper function to get price based on currency and billing cycle
  const getPrice = (item, itemType) => {
    if (!item) return 0;
    if (itemType === 'plan') {
      if (billingCycle === 'yearly') {
        return currency === 'USD' 
          ? (item.price_yearly_usd || 0) 
          : (item.price_yearly_inr || 0);
      }
      return currency === 'USD' 
        ? (item.price_monthly_usd || 0) 
        : (item.price_monthly_inr || 0);
    }
    // Credit pack
    return currency === 'USD' 
      ? (item.price_usd || 0) 
      : (item.price_inr || item.price || 0);
  };
  
  // Helper function to format price
  const formatPrice = (amount) => {
    if (currency === 'USD') {
      return `$${amount.toLocaleString('en-US')}`;
    }
    return `₹${amount.toLocaleString('en-IN')}`;
  };
  
  // Handle currency change
  const handleCurrencyChange = (newCurrency) => {
    setCurrencyState(newCurrency);
    onCurrencyChange?.(newCurrency);
  };
  
  // Handle billing cycle change
  const handleBillingCycleChange = (newCycle) => {
    setBillingCycleState(newCycle);
    onBillingCycleChange?.(newCycle);
  };
  
  useEffect(() => {
    loadRazorpayScript();
  }, []);
  
  const handlePayment = async () => {
    setLoading(true);
    setError('');
    
    try {
      // Create order
      const orderPayload = {
        user_id: user?.id || user?.email || 'guest',
        user_email: user?.email || 'guest@example.com',
        user_name: user?.name || 'Guest User',
        billing_cycle: billingCycle,
        currency: currency
      };
      
      if (activePlan) {
        orderPayload.plan_id = activePlan.id;
      } else if (activeCreditPack) {
        orderPayload.credit_pack_id = activeCreditPack.id;
      }
      
      const orderResponse = await fetch(`${API_URL}/api/payments/create-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(orderPayload)
      });
      
      if (!orderResponse.ok) {
        const errData = await orderResponse.json();
        throw new Error(errData.detail || 'Failed to create order');
      }
      
      const orderData = await orderResponse.json();
      
      // Open Razorpay checkout
      const options = {
        key: orderData.key_id,
        amount: orderData.amount * 100,
        currency: orderData.currency,
        name: 'DecX AI',
        description: orderData.description,
        order_id: orderData.order_id,
        handler: async function (response) {
          // Show payment processing loader
          setProcessingPayment(true);
          
          // Verify payment
          try {
            const verifyResponse = await fetch(`${API_URL}/api/payments/verify`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                user_id: user?.id || user?.email || 'guest'
              })
            });
            
            const verifyData = await verifyResponse.json();
            
            // Wait minimum 20 seconds for payment processing display
            await new Promise(resolve => setTimeout(resolve, 20000));
            
            if (verifyData.success) {
              setProcessingPayment(false);
              setPaymentSuccess(true);
              setSuccessData(verifyData);
              if (onSuccess) {
                onSuccess(verifyData);
              }
            } else {
              setProcessingPayment(false);
              setError('Payment verification failed');
            }
          } catch (err) {
            setProcessingPayment(false);
            setError('Payment verification failed');
          }
          setLoading(false);
        },
        prefill: {
          name: orderData.prefill?.name || user?.name || '',
          email: orderData.prefill?.email || user?.email || '',
          contact: ''
        },
        theme: {
          color: '#00D4FF'
        },
        modal: {
          ondismiss: function() {
            setLoading(false);
          }
        }
      };
      
      const razorpay = new window.Razorpay(options);
      razorpay.on('payment.failed', function (response) {
        setError(response.error.description || 'Payment failed');
        setLoading(false);
      });
      razorpay.open();
      
    } catch (err) {
      setError(err.message || 'Something went wrong');
      setLoading(false);
    }
  };
  
  if (!isOpen) return null;
  
  const item = activePlan || activeCreditPack;
  const isSubscription = type === 'subscription' || !!activePlan;
  
  // Calculate price based on currency
  const price = isSubscription 
    ? getPrice(activePlan, 'plan')
    : getPrice(activeCreditPack, 'credit');
  
  const showSelection = (type === 'subscription' && plans.length > 0 && !activePlan) || 
                        (type === 'credits' && creditPacks.length > 0 && !activeCreditPack);
  
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.8)',
          backdropFilter: 'blur(8px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '20px'
        }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          onClick={(e) => e.stopPropagation()}
          style={{
            width: '100%',
            maxWidth: '480px',
            background: theme === 'dark' ? '#1a1a2e' : '#fff',
            borderRadius: '24px',
            overflow: 'hidden',
            boxShadow: '0 24px 48px rgba(0,0,0,0.3)'
          }}
        >
          {/* Header */}
          <div style={{
            background: C.grad,
            padding: '24px',
            position: 'relative'
          }}>
            <button
              onClick={onClose}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                background: 'rgba(255,255,255,0.2)',
                border: 'none',
                borderRadius: '50%',
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer'
              }}
            >
              <XIcon size={18} color="#fff" />
            </button>
            
            <h2 style={{
              fontFamily: F,
              fontSize: '24px',
              fontWeight: 700,
              color: '#fff',
              marginBottom: '8px'
            }}>
              {paymentSuccess ? 'Payment Successful!' : 'Complete Your Purchase'}
            </h2>
            <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.8)' }}>
              {paymentSuccess ? 'Thank you for your purchase' : 'Secure payment powered by Razorpay'}
            </p>
          </div>
          
          {/* Content */}
          <div style={{ padding: '24px' }}>
            {processingPayment ? (
              /* Payment Processing Loader */
              <PaymentLoader 
                amount={price}
                currency={currency}
                minDuration={20000}
              />
            ) : paymentSuccess ? (
              /* Success State */
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', stiffness: 200, damping: 15 }}
                  style={{
                    width: '80px',
                    height: '80px',
                    borderRadius: '50%',
                    background: '#10B981',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 24px'
                  }}
                >
                  <CheckIcon size={40} color="#fff" />
                </motion.div>
                
                <h3 style={{
                  fontFamily: F,
                  fontSize: '20px',
                  fontWeight: 700,
                  marginBottom: '8px',
                  color: theme === 'dark' ? '#fff' : '#1a1a2e'
                }}>
                  {isSubscription ? `${activePlan?.name} Plan Activated!` : 'Credits Added!'}
                </h3>
                
                {successData?.credits_added > 0 && (
                  <p style={{ 
                    fontSize: '16px', 
                    color: C.secondary,
                    marginBottom: '24px'
                  }}>
                    +{successData.credits_added} credits added to your account
                  </p>
                )}
                
                <button
                  onClick={onClose}
                  style={{
                    padding: '14px 32px',
                    borderRadius: '12px',
                    border: 'none',
                    background: C.grad,
                    color: '#fff',
                    fontSize: '15px',
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  Continue to Dashboard
                </button>
              </div>
            ) : (
              /* Order Summary */
              <>
                {/* Plan/Credit Selection UI when no item selected */}
                {showSelection ? (
                  <div style={{ marginBottom: '24px' }}>
                    {/* Header with Toggles */}
                    <div style={{ 
                      display: 'flex', 
                      flexDirection: 'column',
                      gap: '16px',
                      marginBottom: '20px'
                    }}>
                      <h4 style={{
                        fontSize: '14px',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.1em',
                        color: C.secondary
                      }}>
                        {type === 'subscription' ? 'Choose a Plan' : 'Choose Credits'}
                      </h4>
                      
                      {/* Billing & Currency Toggles */}
                      {type === 'subscription' && (
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          flexWrap: 'wrap',
                          gap: '12px'
                        }}>
                          {/* Billing Cycle Toggle */}
                          <div style={{
                            display: 'flex',
                            background: theme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                            borderRadius: '100px',
                            padding: '3px'
                          }}>
                            {['monthly', 'yearly'].map((cycle) => (
                              <button
                                key={cycle}
                                onClick={() => handleBillingCycleChange(cycle)}
                                style={{
                                  padding: '6px 14px',
                                  borderRadius: '100px',
                                  border: 'none',
                                  background: billingCycle === cycle ? C.grad : 'transparent',
                                  color: billingCycle === cycle ? '#fff' : (theme === 'dark' ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)'),
                                  fontWeight: 600,
                                  fontSize: '12px',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s ease',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px'
                                }}
                                data-testid={`checkout-billing-${cycle}`}
                              >
                                {cycle === 'monthly' ? 'Monthly' : 'Yearly'}
                                {cycle === 'yearly' && (
                                  <span style={{
                                    background: '#10B981',
                                    color: '#fff',
                                    padding: '1px 5px',
                                    borderRadius: '4px',
                                    fontSize: '9px',
                                    fontWeight: 700
                                  }}>
                                    -17%
                                  </span>
                                )}
                              </button>
                            ))}
                          </div>
                          
                          {/* Currency Toggle */}
                          <div style={{
                            display: 'flex',
                            background: theme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                            borderRadius: '100px',
                            padding: '3px'
                          }}>
                            {['INR', 'USD'].map((curr) => (
                              <button
                                key={curr}
                                onClick={() => handleCurrencyChange(curr)}
                                style={{
                                  padding: '6px 12px',
                                  borderRadius: '100px',
                                  border: 'none',
                                  background: currency === curr ? (theme === 'dark' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.1)') : 'transparent',
                                  color: currency === curr ? (theme === 'dark' ? '#fff' : '#1a1a2e') : (theme === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)'),
                                  fontWeight: 600,
                                  fontSize: '11px',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s ease'
                                }}
                                data-testid={`checkout-currency-${curr.toLowerCase()}`}
                              >
                                {curr === 'INR' ? '₹ INR' : '$ USD'}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Currency toggle only for credits */}
                      {type === 'credits' && (
                        <div style={{
                          display: 'flex',
                          background: theme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                          borderRadius: '100px',
                          padding: '3px',
                          alignSelf: 'flex-start'
                        }}>
                          {['INR', 'USD'].map((curr) => (
                            <button
                              key={curr}
                              onClick={() => handleCurrencyChange(curr)}
                              style={{
                                padding: '6px 12px',
                                borderRadius: '100px',
                                border: 'none',
                                background: currency === curr ? (theme === 'dark' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.1)') : 'transparent',
                                color: currency === curr ? (theme === 'dark' ? '#fff' : '#1a1a2e') : (theme === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)'),
                                fontWeight: 600,
                                fontSize: '11px',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease'
                              }}
                              data-testid={`checkout-currency-${curr.toLowerCase()}`}
                            >
                              {curr === 'INR' ? '₹ INR' : '$ USD'}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {type === 'subscription' && plans.map((p) => (
                        <motion.div
                          key={p.id}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => onPlanSelect?.(p)}
                          style={{
                            padding: '16px',
                            borderRadius: '12px',
                            background: theme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                            border: `2px solid ${plan?.id === p.id ? C.secondary : 'transparent'}`,
                            cursor: 'pointer',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}
                          data-testid={`select-plan-${p.id}`}
                        >
                          <div>
                            <div style={{ 
                              fontSize: '16px', 
                              fontWeight: 600, 
                              color: theme === 'dark' ? '#fff' : '#1a1a2e',
                              marginBottom: '4px'
                            }}>
                              {p.name}
                            </div>
                            <div style={{ 
                              fontSize: '13px', 
                              color: theme === 'dark' ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)' 
                            }}>
                              {p.credits} credits/month
                            </div>
                          </div>
                          <div style={{ 
                            fontSize: '18px', 
                            fontWeight: 700, 
                            color: C.secondary 
                          }}>
                            {formatPrice(getPrice(p, 'plan'))}
                          </div>
                        </motion.div>
                      ))}
                      
                      {type === 'credits' && creditPacks.map((cp) => (
                        <motion.div
                          key={cp.id}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => onCreditPackSelect?.(cp)}
                          style={{
                            padding: '16px',
                            borderRadius: '12px',
                            background: theme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                            border: `2px solid ${creditPack?.id === cp.id ? C.secondary : 'transparent'}`,
                            cursor: 'pointer',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}
                          data-testid={`select-credits-${cp.id}`}
                        >
                          <div style={{ 
                            fontSize: '16px', 
                            fontWeight: 600, 
                            color: theme === 'dark' ? '#fff' : '#1a1a2e' 
                          }}>
                            {cp.credits} Credits
                          </div>
                          <div style={{ 
                            fontSize: '18px', 
                            fontWeight: 700, 
                            color: C.secondary 
                          }}>
                            {formatPrice(getPrice(cp, 'credit'))}
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                ) : (
                <div style={{
                  background: theme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                  borderRadius: '16px',
                  padding: '20px',
                  marginBottom: '24px'
                }}>
                  <h4 style={{
                    fontSize: '14px',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                    color: C.secondary,
                    marginBottom: '16px'
                  }}>
                    Order Summary
                  </h4>
                  
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    marginBottom: '12px'
                  }}>
                    <span style={{ 
                      fontSize: '16px',
                      color: theme === 'dark' ? '#fff' : '#1a1a2e'
                    }}>
                      {isSubscription 
                        ? `${activePlan?.name} Plan (${billingCycle})`
                        : `${activeCreditPack?.credits} Credits`
                      }
                    </span>
                    <span style={{ 
                      fontSize: '16px', 
                      fontWeight: 600,
                      color: theme === 'dark' ? '#fff' : '#1a1a2e'
                    }}>
                      {formatPrice(price)}
                    </span>
                  </div>
                  
                  {isSubscription && activePlan?.credits > 0 && (
                    <div style={{
                      fontSize: '13px',
                      color: C.secondary,
                      marginTop: '8px'
                    }}>
                      Includes {activePlan.credits} credits per {billingCycle === 'yearly' ? 'year' : 'month'}
                    </div>
                  )}
                  
                  <div style={{
                    borderTop: theme === 'dark' ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)',
                    marginTop: '16px',
                    paddingTop: '16px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <span style={{ 
                      fontSize: '18px', 
                      fontWeight: 700,
                      color: theme === 'dark' ? '#fff' : '#1a1a2e'
                    }}>
                      Total
                    </span>
                    <span style={{ 
                      fontSize: '24px', 
                      fontWeight: 700,
                      color: C.secondary
                    }}>
                      {formatPrice(price)}
                    </span>
                  </div>
                </div>
                )}
                
                {/* Error Message */}
                {error && (
                  <div style={{
                    background: 'rgba(239,68,68,0.1)',
                    border: '1px solid rgba(239,68,68,0.3)',
                    borderRadius: '12px',
                    padding: '12px 16px',
                    marginBottom: '20px',
                    color: '#EF4444',
                    fontSize: '14px'
                  }}>
                    {error}
                  </div>
                )}
                
                {/* Pay Button */}
                <button
                  onClick={handlePayment}
                  disabled={loading || showSelection}
                  style={{
                    width: '100%',
                    padding: '16px',
                    borderRadius: '12px',
                    border: 'none',
                    background: (loading || showSelection) ? 'rgba(128,128,128,0.3)' : C.grad,
                    color: '#fff',
                    fontSize: '16px',
                    fontWeight: 600,
                    cursor: (loading || showSelection) ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '10px'
                  }}
                  data-testid="checkout-pay-btn"
                >
                  {loading ? (
                    <>
                      <Loader2Icon size={20} />
                      Processing...
                    </>
                  ) : showSelection ? (
                    <>
                      <CreditCardIcon size={20} />
                      Select {type === 'subscription' ? 'a Plan' : 'Credits'} to Continue
                    </>
                  ) : (
                    <>
                      <CreditCardIcon size={20} />
                      Pay {formatPrice(price)}
                    </>
                  )}
                </button>
                
                {/* Security Badge */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  marginTop: '20px',
                  color: theme === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                  fontSize: '13px'
                }}>
                  <ShieldIcon size={16} />
                  <span>Secured by Razorpay • 256-bit SSL</span>
                </div>
              </>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default PaymentCheckout;
